/* eslint-disable react/prop-types */
import { motion } from 'framer-motion';
import { inject, observer } from "mobx-react";
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import Plus from '../svg/Plus';
import Minus from '../svg/Minus';

/**
 * Unified QuantityControls component that handles all variations
 *
 * @param {Object} props - Component props
 * @param {Object} props.studioStore - MobX studio store (from inject)
 * @param {Function} props.handleRemoveFromCart - Function to handle cart item removal
 * @param {Function} props.handleAddToCart - Function to handle adding to cart
 * @param {Function} props.handleUpdateQuantity - Function to handle quantity updates
 * @param {number} props.quantity - Current quantity
 * @param {string} props.productDisplayType - Display type text for the product
 * @param {boolean} props.productWeightQuantity - Whether to divide quantity by 2 for display
 * @param {boolean} props.compact - Whether to use compact layout
 * @param {Object} props.product - Product object
 * @param {string} props.variant - Component variant ('cart', 'internal', 'mobile', 'v2')
 * @param {boolean} props.disableInput - Whether to disable input interaction
 */
const QuantityControls = inject('studioStore')(observer(({
  studioStore,
  handleRemoveFromCart,
  handleAddToCart,
  handleUpdateQuantity,
  quantity,
  productDisplayType,
  productWeightQuantity = false,
  compact = false,
  product,
  variant = 'cart',
  disableInput = false,
}) => {
  const t = useTranslations('Product');
  const [direction, setDirection] = useState(0);

  // Get design properties based on variant
  const cartDesign = studioStore?.cart;
  const secondaryTextColor = studioStore?.settings?.colors?.text_secondary_color || studioStore?.colors?.secondary_text_color;

  // Check if product is in stock
  const isInStock = !product || product.stock_status === 'instock';

  // If product is not in stock, show out of stock message
  if (!isInStock) {
    const outOfStockStyle = {
      color: variant === 'v2' ? 'white' : secondaryTextColor || '#888',
      backgroundColor: variant === 'v2' ? 'transparent' : '#f8f8f8',
      padding: compact ? '0.25rem 0.5rem' : '0.5rem 1rem',
      borderRadius: '0.25rem',
      fontWeight: '500',
      display: 'inline-block',
      textAlign: 'center',
      width: '100%',
    };

    return (
      <div className="quantity-controls-out-of-stock" style={outOfStockStyle}>
        {t('out_of_stock')}
      </div>
    );
  }

  const handleInputChange = (e) => {
    const newQuantity = parseInt(e.target.value, 10);
    if (!isNaN(newQuantity)) {
      handleUpdateQuantity(newQuantity);
    }
  };

  const addToCartHandler = () => {
    setDirection(1);
    handleAddToCart();
  };

  const removeFromCartHandler = () => {
    setDirection(-1);
    handleRemoveFromCart();
  };

  const numberVariants = {
    enter: (direction) => ({
      y: direction > 0 ? 20 : -20, // Slide from bottom if increasing, top if decreasing
      opacity: 0,
    }),
    center: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.2, ease: "easeInOut" }, // Slightly longer to handle rapid changes
    },
    exit: (direction) => ({
      y: direction > 0 ? -20 : 20, // Slide to top if increasing, bottom if decreasing
      opacity: 0,
      transition: { duration: 0.1, ease: "easeInOut" },
    }),
  };

  // Variant-specific styling and layout
  const getVariantStyles = () => {
    switch (variant) {
      case 'internal':
        return {
          container: 'quantity-controls',
          wrapper: 'relative flex flex-1 items-center justify-start text-center max-w-[9rem]',
          plusButton: 'p-2 h-8',
          minusButton: 'p-2 h-8',
          inputWrapper: 'relative w-10 h-6 flex justify-center items-center',
          input: `bg-transparent h-12 font-medium text-center text-xl block w-full`,
          iconSize: 'w-2 h-2',
          iconColor: secondaryTextColor ? { color: secondaryTextColor } : {},
          containerStyle: secondaryTextColor ? { color: secondaryTextColor } : {},
        };
      case 'modal':
        return {
          container: 'quantity-controls',
          wrapper: 'relative flex flex-1 items-center justify-start text-center max-w-[9rem]',
          plusButton: 'p-[9px] h-[2rem] w-[2rem] border border-color-secondary-text-studio rounded-full',
          minusButton: 'p-[9px] h-[2rem] w-[2rem] border border-color-secondary-text-studio rounded-full',
          inputWrapper: 'relative w-[2.75rem] h-6 flex justify-center items-center',
          input: `bg-transparent h-12 font-medium text-center text-xl block w-full`,
          iconSize: 'w-[0.75rem] h-[0.75rem]',
          iconColor: secondaryTextColor ? { color: secondaryTextColor } : {},
          containerStyle: secondaryTextColor ? { color: secondaryTextColor } : {},
          displayType: {
            wrapper: 'hidden',
          },
        };
      case 'mobile':
        return {
          container: 'quantity-controls',
          wrapper: 'relative flex flex-1 items-center justify-center text-center max-w-[5.65rem]',
          plusButton: 'px-[0.35rem] py-2 h-full w-[27.5%]',
          minusButton: 'px-[0.35rem] py-2 h-8 w-[27.5%]',
          inputWrapper: 'relative w-[45%] h-10 flex justify-center items-center',
          input: `bg-transparent h-12 font-medium text-center text-black text-xl block w-full ${disableInput ? 'pointer-events-none' : ''}`,
          iconSize: 'w-full h-full',
          iconColor: { color: 'black' },
          displayType: {
            wrapper: 'hidden',
          },
        };
      case 'v2':
        return {
          container: `quantity-controls ${compact ? 'p-0' : 'px-4 py-3'}`,
          wrapper: 'relative flex-1 items-center justify-center text-center max-w-[9rem]',
          plusButton: 'p-2 h-8',
          minusButton: 'p-2 h-8',
          inputWrapper: 'relative w-20 h-12 flex justify-center items-center',
          input: `h-12 font-medium text-center text-white text-xl block w-full${productDisplayType ? ' pb-4' : ''}`,
          inputStyle: { backgroundColor: 'rgba(255, 255, 255, 0)' },
          iconSize: 'w-4 h-4',
          iconColor: { color: 'white' },
          displayType: {
            wrapper: 'absolute bottom-1 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 flex items-center text-white space-x-1',
          },
        };
      case 'cart':
      default:
        return {
          container: `quantity-controls ${compact ? 'p-0' : 'p-4'}`,
          wrapper: 'relative flex flex-1 items-center justify-center text-center max-w-[9rem]',
          plusButton: 'p-2 h-8',
          minusButton: 'p-2 h-8',
          inputWrapper: 'relative w-10 h-10 flex justify-center items-center',
          input: `bg-transparent h-12 font-medium text-center text-black text-xl block w-full`,
          iconSize: 'w-2 h-2',
          iconColor: { color: 'black' },
          displayType: {
            wrapper: 'flex justify-evenly items-center text-black space-x-1 max-w-[9rem]',
            text: `${cartDesign?.product?.border_radius} w-20 py-1 text-xs text-center text-primary-studio bg-primary-light-studio`,
          },
        };
    }
  };

  const styles = getVariantStyles();

  // Special case for rendering without a container div
  if (variant === 'mobile') {
    return (
      <>
        {productDisplayType && (
          <div className={styles.displayType.wrapper}>
            <span className={styles.displayType.text} style={styles.displayType.style}>
              {productDisplayType}
            </span>
          </div>
        )}
        <div className={styles.wrapper}>
          <div className="flex items-center">
            <motion.button
              onClick={addToCartHandler}
              whileTap={{ scale: 0.9 }}
              className={styles.plusButton}
              title={t('add_to_cart')}
              data-testid={product?.id ? `add-to-cart-btn-${product.id}` : undefined}
            >
              <Plus className={styles.iconSize} stroke={styles.iconColor.color} />
            </motion.button>

            <div className={styles.inputWrapper}>
              <motion.div
                key={`quantity-${quantity}`}
                custom={direction}
                variants={numberVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="absolute w-full h-full flex items-center justify-center"
              >
                <input
                  type="number"
                  value={productWeightQuantity ? quantity / 2 : quantity}
                  onChange={handleInputChange}
                  min="0"
                  name="quantity"
                  className={styles.input}
                  style={styles.inputStyle}
                  title={t('quantity')}
                  data-testid={product?.id ? `quantity-input-${product.id}` : undefined}
                />
              </motion.div>
            </div>

            <motion.button
              onClick={removeFromCartHandler}
              whileTap={{ scale: 0.9 }}
              className={styles.minusButton}
              style={{
                visibility: quantity > 0 ? 'visible' : 'hidden',
                ...(styles.iconColor || {}),
              }}
              title={t('remove_from_cart')}
              data-testid={product?.id ? `remove-from-cart-btn-${product.id}` : undefined}
            >
              <Minus className={styles.iconSize} stroke={styles.iconColor.color} />
            </motion.button>
          </div>
        </div>
      </>
    );
  }

  // For v2 variant with product display type in different position
  if (variant === 'v2' && productDisplayType) {
    return (
      <div className={styles.container} style={styles.containerStyle}>
        <div className={styles.wrapper}>
          <div className="flex items-center">
            <motion.button
              onClick={addToCartHandler}
              whileTap={{ scale: 0.9 }}
              className={styles.plusButton}
              title={t('add_to_cart')}
              data-testid={product?.id ? `add-to-cart-btn-${product.id}` : undefined}
            >
              <Plus className={styles.iconSize} stroke={styles.iconColor.color} />
            </motion.button>

            <div className={styles.inputWrapper}>
              <motion.div
                key={`quantity-${quantity}`}
                custom={direction}
                variants={numberVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="absolute w-full h-full flex items-center justify-center"
              >
                <input
                  type="number"
                  value={productWeightQuantity ? quantity / 2 : quantity}
                  onChange={handleInputChange}
                  min="0"
                  name="quantity"
                  className={styles.input}
                  style={styles.inputStyle}
                  title={t('quantity')}
                  data-testid={product?.id ? `quantity-input-${product.id}` : undefined}
                />
              </motion.div>
            </div>

            {productDisplayType && (
              <div className={styles.displayType.wrapper}>
                <span>{productDisplayType}</span>
              </div>
            )}

            <motion.button
              onClick={removeFromCartHandler}
              whileTap={{ scale: 0.9 }}
              className={styles.minusButton}
              style={{
                visibility: quantity > 0 ? 'visible' : 'hidden',
                ...(styles.iconColor || {}),
              }}
              title={t('remove_from_cart')}
              data-testid={product?.id ? `remove-from-cart-btn-${product.id}` : undefined}
            >
              <Minus className={styles.iconSize} stroke={styles.iconColor.color} />
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  // Standard rendering for cart and internal variants
  return (
    <div className={styles.container} style={styles.containerStyle}>
      {productDisplayType && variant !== 'v2' && (
        <div className={styles.displayType?.wrapper || "flex justify-evenly items-center text-black space-x-1"}>
          <span
            className={styles.displayType?.text || ""}
            style={styles.displayType?.style || {}}
          >
            {productDisplayType}
          </span>
        </div>
      )}
      <div className={styles.wrapper}>
        <div className="flex items-center">
          <motion.button
            onClick={addToCartHandler}
            whileTap={{ scale: 0.9 }}
            className={styles.plusButton}
            title={t('add_to_cart')}
            data-testid={product?.id ? `add-to-cart-btn-${product.id}` : undefined}
          >
            <Plus className={styles.iconSize} stroke={styles.iconColor.color} />
          </motion.button>

          <div className={styles.inputWrapper}>
            <motion.div
              key={`quantity-${quantity}`}
              custom={direction}
              variants={numberVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute w-full h-full flex items-center justify-center"
            >
              <input
                type="number"
                value={productWeightQuantity ? quantity / 2 : quantity}
                onChange={handleInputChange}
                min="0"
                name="quantity"
                className={styles.input}
                style={styles.inputStyle}
                title={t('quantity')}
                data-testid={product?.id ? `quantity-input-${product.id}` : undefined}
              />
            </motion.div>
          </div>

          <motion.button
            onClick={removeFromCartHandler}
            whileTap={{ scale: 0.9 }}
            className={styles.minusButton}
            style={{
              visibility: quantity > 0 ? 'visible' : 'hidden',
              ...(styles.iconColor || {}),
            }}
            title={t('remove_from_cart')}
            data-testid={product?.id ? `remove-from-cart-btn-${product.id}` : undefined}
          >
            <Minus className={styles.iconSize} stroke={styles.iconColor.color} />
          </motion.button>
        </div>
      </div>
    </div>
  );
}));

export default QuantityControls;
